/**
  API Key API wrapper

	JSON endpoint:  /aws_api/manage.json
	GET  /aws_api/manage.json?loid={loid} - Get current API key
	POST  /aws_api/manage.json?loid={loid} - Create new API key
	DELETE  /aws_api/manage.json?id={id} - Delete API key
**/

import { api, getQueryString } from "../common/utils"
import {
  AUTO_LOAN_RATE_TABLE_CONFIG_ENDPOINT,
  AUTO_LOAN_RATE_TABLE_CONFIGS_ENDPOINT,
  FIND_COUNTY_ENDPOINT,
  HELOC_RATE_TABLE_CONFIG_ENDPOINT,
  HELOC_RATE_TABLE_CONFIGS_ENDPOINT,
  RATE_TABLE_CONFIG_ENDPOINT,
  SUBSCRIPTION_CONFIG_ENDPOINT
} from "../common/constants"
import {
  AutoLoanRateTableConfig,
  AutoLoanRateTableConfigDetails,
  AutoLoanRateTableConfigsListResponse,
  AutoLoanRateTableConfigUpsertPayload,
  CountyDetails,
  HelocRateTableConfig,
  HelocRateTableConfigDetails,
  HelocRateTableConfigsListResponse,
  HelocRateTableConfigUpsertPayload,
  RateTableConfig,
  RateTableConfigDetails,
  SubscriptionConfigDetails
} from "../types"

const configAdmin = {
  getRateTableConfigs: async (params: any): Promise<RateTableConfig[]> => {
    const { data } = await api.get(
      `${RATE_TABLE_CONFIG_ENDPOINT}${getQueryString(params)}`
    )
    return data
  },
  getRateTableConfigDetails: async (
    id: string
  ): Promise<RateTableConfigDetails> => {
    const { data } = await api.get(
      `${RATE_TABLE_CONFIG_ENDPOINT}${getQueryString({ id })}`
    )
    return data
  },
  getDefaultRateTableConfig: async (): Promise<RateTableConfigDetails> => {
    const { data } = await api.get(`${RATE_TABLE_CONFIG_ENDPOINT}?id=`)
    return data
  },
  updateRateTableConfig: async (
    payload: Partial<RateTableConfigDetails>
  ): Promise<{ status: string; message?: string }> => {
    const { data } = await api.post(RATE_TABLE_CONFIG_ENDPOINT, payload)
    return data
  },
  deleteRateTableConfig: async (
    id: string
  ): Promise<{ status: string; message?: string }> => {
    const { data } = await api.delete(`${RATE_TABLE_CONFIG_ENDPOINT}?id=${id}`)
    return data
  },
  getHelocRateTableConfigs: async (params: {
    brand_id?: string
    limit?: number
    offset?: number
  }): Promise<HelocRateTableConfig[]> => {
    const { data } = await api.get<HelocRateTableConfigsListResponse>(
      `${HELOC_RATE_TABLE_CONFIGS_ENDPOINT}${getQueryString(params)}`
    )
    return data?.heloc_rate_table_configs ?? []
  },
  getHelocRateTableConfigDetails: async (
    id: string
  ): Promise<HelocRateTableConfigDetails> => {
    const { data } = await api.get(
      `${HELOC_RATE_TABLE_CONFIG_ENDPOINT}${getQueryString({ id })}`
    )
    return data
  },
  getDefaultHelocRateTableConfig:
    async (): Promise<HelocRateTableConfigDetails> => {
      const { data } = await api.get(`${HELOC_RATE_TABLE_CONFIG_ENDPOINT}?id=`)
      return data
    },
  updateHelocRateTableConfig: async (
    payload: HelocRateTableConfigUpsertPayload
  ): Promise<{ status: string; message?: string; id?: string }> => {
    const { data } = await api.post(HELOC_RATE_TABLE_CONFIG_ENDPOINT, payload)
    return data
  },
  deleteHelocRateTableConfig: async (
    id: string
  ): Promise<{ status: string; message?: string }> => {
    const { data } = await api.delete(
      `${HELOC_RATE_TABLE_CONFIG_ENDPOINT}?id=${id}`
    )
    return data
  },
  getAutoLoanRateTableConfigs: async (params: {
    brand_id?: string
    limit?: number
    offset?: number
  }): Promise<AutoLoanRateTableConfig[]> => {
    const { data } = await api.get<AutoLoanRateTableConfigsListResponse>(
      `${AUTO_LOAN_RATE_TABLE_CONFIGS_ENDPOINT}${getQueryString(params)}`
    )
    return data?.auto_loan_rate_table_configs ?? []
  },
  getAutoLoanRateTableConfigDetails: async (
    id: string
  ): Promise<AutoLoanRateTableConfigDetails> => {
    const { data } = await api.get(
      `${AUTO_LOAN_RATE_TABLE_CONFIG_ENDPOINT}${getQueryString({ id })}`
    )
    return data
  },
  getDefaultAutoLoanRateTableConfig:
    async (): Promise<AutoLoanRateTableConfigDetails> => {
      const { data } = await api.get(
        `${AUTO_LOAN_RATE_TABLE_CONFIG_ENDPOINT}?id=`
      )
      return data
    },
  updateAutoLoanRateTableConfig: async (
    payload: AutoLoanRateTableConfigUpsertPayload
  ): Promise<{ status: string; message?: string; id?: string }> => {
    const { data } = await api.post(
      AUTO_LOAN_RATE_TABLE_CONFIG_ENDPOINT,
      payload
    )
    return data
  },
  deleteAutoLoanRateTableConfig: async (
    id: string
  ): Promise<{ status: string; message?: string }> => {
    const { data } = await api.delete(
      `${AUTO_LOAN_RATE_TABLE_CONFIG_ENDPOINT}?id=${id}`
    )
    return data
  },
  getCountyOptions: async (name_like: string): Promise<string[]> => {
    const { data } = await api.get(
      `${FIND_COUNTY_ENDPOINT}${getQueryString({ name_like })}`
    )
    return data
  },
  getCountyDetails: async (params: {
    name: string
    state: string
  }): Promise<CountyDetails> => {
    const { data } = await api.get(
      `${FIND_COUNTY_ENDPOINT}${getQueryString(params)}`
    )
    return data
  },
  updateCounty: async (
    payload: Partial<CountyDetails>
  ): Promise<{ status: string; message?: string }> => {
    const { data } = await api.post(FIND_COUNTY_ENDPOINT, payload)
    return data
  },
  updateSubscriptionConfig: async (
    payload: Partial<SubscriptionConfigDetails>
  ): Promise<{ status: string; message?: string }> => {
    const { data } = await api.post(SUBSCRIPTION_CONFIG_ENDPOINT, payload)
    return data
  },
  deleteSubscriptionConfig: async (
    id: string
  ): Promise<{ status: string; message?: string }> => {
    const { data } = await api.delete(
      `${SUBSCRIPTION_CONFIG_ENDPOINT}?id=${id}`
    )
    return data
  }
}

export default configAdmin
